import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { T, useT } from "../i18n";
import type { Uebersetzer } from "../i18n";
import { FehlerBand, Knopf } from "./index";
import { Blattvonunten } from "./Blattvonunten";
import { Datum } from "./Zeitraum";
import { useRuf } from "../schale/Ruf";
import { Fehler } from "../daten/klient";
import { alsTag, zweistellig } from "../daten/kalender";
import { datumKurz, uhr } from "../daten/einsatzzeit";
import { useAufnahmeTermin } from "../daten/haken";
import type { Aufnahmetermin } from "../daten/typen";
import { heuteImBetrieb, tagImBetrieb, wandAlsZeitpunkt } from "../daten/betriebstag";
import { kwVon } from "../daten/kalender";

/* ══════════════════════════════════════════════════════════════════
   Der Termin einer Ortsaufnahme — auf Wunsch (Migration 0180, 24.09.2026).

   19-ortsaufnahme.md hat es von Anfang an so stehen: *„Der Termin ist
   optional und kommt vom Vertriebler. Macht er einen festen, entsteht
   daraus eine Belegung."* Gebaut wurde bis heute nur die erste Hälfte —
   die Aufgabe ohne Datum. Luca, 24.09.2026: *„und ja bau das"*.

   **Die Aufnahme bleibt eine Aufgabe.** Ohne Termin steht hier ein Satz
   und ein Knopf, sonst nichts: Kein Feld fragt nach einem Datum, das
   niemand braucht (Regel 11). Mit Termin steht er da, wie er im Band
   steht — Tag, Uhrzeit, Person —, und daneben ändern und entfernen.

   **Das Blatt fragt drei Dinge und belegt sie vor:** morgen, 09:00 bis
   10:00. Wer fährt, fragt es nicht — das ist, wer die Aufnahme hat, sonst
   der, der den Termin einträgt (`PUT /aufnahmen/:id/termin`).

   Dasselbe Stück steht am Schreibtisch im Blatt der Liste
   (`Aufnahmeblatt`) und in der Begehung (`handy/Ortsbegehung.tsx`) — ein
   Termin, eine Zeichnung.
   ══════════════════════════════════════════════════════════════════ */

/** Tag, Anfang, Ende und Person als Werte für die Sätze — in der Uhr des Geräts. */
export function terminWerte(termin: Aufnahmetermin): Record<string, string> {
  return { tag: datumKurz(termin.von), von: uhr(termin.von), bis: uhr(termin.bis), person: termin.person };
}

/** „Termin 25.09. · 09:00–10:00" — die kurze Form für eine Listenzeile. */
export function terminKurz(t: Uebersetzer, termin: Aufnahmetermin): string {
  return t("ortsbegehung.terminKurz", terminWerte(termin));
}

function fehlerPfad(e: unknown): string {
  return e instanceof Fehler ? e.textPfad : "fehler.unbekannt";
}

/** Der Entwurf im Blatt: ein Tag und zwei Uhrzeiten, so wie die Felder sie halten. */
type Entwurf = { tag: string | null; von: string; bis: string };

/** Vorbelegung (Regel 11): der bestehende Termin — sonst morgen, 09:00 bis 10:00. */
function entwurfAus(termin: Aufnahmetermin | null): Entwurf {
  if (termin) {
    return { tag: tagImBetrieb(termin.von), von: uhr(termin.von), bis: uhr(termin.bis) };
  }
  const morgen = new Date();
  morgen.setDate(morgen.getDate() + 1);
  return { tag: alsTag(morgen), von: `${zweistellig(9)}:00`, bis: `${zweistellig(10)}:00` };
}

/**
 * Tag und Uhrzeit in deutscher Zeit → ein Zeitpunkt mit Zone. Wer „9 Uhr"
 * eintippt, meint 9 Uhr im Betrieb — dieselbe Uhr, in der die Zeile den
 * Termin zeigt (N8-02).
 */
function zeitpunkt(tag: string, uhrzeit: string): string {
  return wandAlsZeitpunkt(tag, uhrzeit).toISOString();
}

/**
 * Der Abschnitt „Termin" einer offenen Aufnahme. Am Schreibtisch ein Teil des
 * Blattes (mit Schild), in der Begehung eine Karte unter einer Überschrift,
 * die der Abschnitt drumherum trägt (`klasse="ob-karte"`, `schild={false}`).
 */
export function Terminteil({ aufnahmeId, termin, klasse = "brett-blatt-teil", schild = true }: {
  aufnahmeId: string;
  termin: Aufnahmetermin | null;
  klasse?: string;
  schild?: boolean;
}) {
  const t = useT();
  const ruf = useRuf();
  const aendern = useAufnahmeTermin();
  const [offen, setzeOffen] = useState(false);

  return (
    <div className={klasse}>
      {schild ? <span className="feld-schild">{t("aufnahmen.terminSchild")}</span> : null}
      {termin ? (
        <>
          <p className="blatt-satz">
            <b>{t("aufnahmen.terminWann", terminWerte(termin))}</b>
            {termin.person ? <> · {t("aufnahmen.terminBei", { person: termin.person })}</> : null}
          </p>
          <p className="blatt-satz" style={{ marginTop: 4, fontSize: 13 }}>
            <T k="aufnahmen.terminSatz" werte={{ person: termin.person }} />
          </p>
        </>
      ) : (
        <p className="blatt-satz" style={{ fontSize: 13 }}>{t("aufnahmen.terminOhneSatz")}</p>
      )}
      <div className="knopfreihe" style={{ marginTop: 8 }}>
        <Knopf klein onClick={() => setzeOffen(true)}>
          {t(termin ? "aufnahmen.terminAendern" : "aufnahmen.terminSetzen")}
        </Knopf>
        {termin ? (
          <Knopf
            klein
            art="stumm"
            disabled={aendern.isPending}
            onClick={() => aendern.mutate(
              { id: aufnahmeId, termin: null },
              { onSuccess: () => ruf("terminEntfernt") },
            )}
          >
            {t("aufnahmen.terminEntfernen")}
          </Knopf>
        ) : null}
      </div>
      {aendern.isError && !offen ? <FehlerBand pfad={fehlerPfad(aendern.error)} fehler={aendern.error} /> : null}
      {/* Neu gemountet bei jedem Öffnen: Der Entwurf beginnt beim Stand,
          nicht bei dem, was beim letzten Mal halb getippt liegen blieb. */}
      {offen ? (
        <Terminblatt aufnahmeId={aufnahmeId} termin={termin} schliessen={() => setzeOffen(false)} />
      ) : null}
    </div>
  );
}

/**
 * **Am Schreibtisch wird über Einteilen geplant** (Paket aufnahmen2,
 * 08.10.2026). Luca: Termin setzen und Person wählen gehen über Einteilen —
 * dort steht, wer an dem Tag frei ist. Statt des eigenen Terminblatts steht
 * hier der Stand als Text (Termin und Person, sonst der Wunsch, sonst „noch
 * nicht geplant") und der Verweis „In Einteilen planen" bzw. „umplanen", der
 * die Woche des Termins oder Wunschs öffnet und die Aufnahme markiert
 * (`/einteilen?tag=…&aufnahme=…`). Am Telefon bleibt `Terminteil`.
 */
export function TerminInEinteilen({ aufnahmeId, termin, wunsch, wer, klasse = "brett-blatt-teil", schild = true }: {
  aufnahmeId: string;
  termin: Aufnahmetermin | null;
  wunsch?: { von: string; bis: string; schaerfe: "genau" | "kw" } | null | undefined;
  wer?: string | null | undefined;
  klasse?: string;
  schild?: boolean;
}) {
  const t = useT();
  const gehZu = useNavigate();
  const tag = termin ? tagImBetrieb(termin.von) : wunsch ? tagImBetrieb(wunsch.von) : heuteImBetrieb();
  const geplant = Boolean(termin || wer);
  return (
    <div className={klasse}>
      {schild ? <span className="feld-schild">{t("aufnahmen.terminSchild")}</span> : null}
      <p className="blatt-satz">
        {termin ? (
          <>
            <b>{t("aufnahmen.terminWann", terminWerte(termin))}</b>
            {termin.person ? <> · {t("aufnahmen.terminBei", { person: termin.person })}</> : null}
          </>
        ) : wunsch ? (
          <>
            <b>{wunsch.schaerfe === "kw"
              ? t("aufnahmen.wunschWoche", { kw: kwVon(new Date(`${tagImBetrieb(wunsch.von)}T12:00:00`)) })
              : t("aufnahmen.wunschTag", { tag: datumKurz(wunsch.von), von: uhr(wunsch.von) })}</b>
            {wer ? <> · {t("aufnahmen.terminBei", { person: wer })}</> : null}
          </>
        ) : wer ? (
          t("aufnahmen.einteilenBeiOhneTermin", { person: wer })
        ) : (
          t("aufnahmen.einteilenNochNicht")
        )}
      </p>
      <div className="knopfreihe" style={{ marginTop: 8 }}>
        <Knopf klein onClick={() => void gehZu({ to: "/einteilen", search: { tag, aufnahme: aufnahmeId } })}>
          {t(geplant ? "aufnahmen.einteilenUmplanen" : "aufnahmen.einteilenPlanen")}
        </Knopf>
      </div>
    </div>
  );
}

/** Das Blatt von unten: Tag, von, bis — vorbelegt, ein Knopf. */
function Terminblatt({ aufnahmeId, termin, schliessen }: {
  aufnahmeId: string;
  termin: Aufnahmetermin | null;
  schliessen: () => void;
}) {
  const t = useT();
  const ruf = useRuf();
  const aendern = useAufnahmeTermin();
  const [entwurf, setzeEntwurf] = useState<Entwurf>(() => entwurfAus(termin));

  const gueltig = !!entwurf.tag && !!entwurf.von && !!entwurf.bis && entwurf.bis > entwurf.von;

  const sichern = () => {
    if (!gueltig || !entwurf.tag) return;
    aendern.mutate(
      {
        id: aufnahmeId,
        termin: { von: zeitpunkt(entwurf.tag, entwurf.von), bis: zeitpunkt(entwurf.tag, entwurf.bis) },
      },
      { onSuccess: () => { ruf("terminGesetzt"); schliessen(); } },
    );
  };

  return (
    <Blattvonunten
      offen
      schliessen={schliessen}
      titel={t("aufnahmen.terminBlattTitel")}
      rast="gross"
      fuss={(
        <Knopf art="haupt" disabled={!gueltig || aendern.isPending} onClick={sichern}>
          {t("aufnahmen.terminSichern")}
        </Knopf>
      )}
    >
      <div className="brett-blatt-teil">
        <span className="feld-schild">{t("aufnahmen.terminTag")}</span>
        <Datum
          id="termin-tag"
          wert={entwurf.tag}
          richtung="vor"
          schild={t("aufnahmen.terminTag")}
          aendern={(tag) => setzeEntwurf((e) => ({ ...e, tag }))}
        />
        <div className="zr-zeitpunkt" style={{ marginTop: 10 }}>
          <label style={{ flex: 1 }}>
            <span className="feld-schild">{t("aufnahmen.terminVon")}</span>
            <input
              className="feld zr-uhr"
              type="time"
              value={entwurf.von}
              onChange={(e) => setzeEntwurf((alt) => ({ ...alt, von: e.target.value }))}
            />
          </label>
          <label style={{ flex: 1 }}>
            <span className="feld-schild">{t("aufnahmen.terminBis")}</span>
            <input
              className="feld zr-uhr"
              type="time"
              value={entwurf.bis}
              onChange={(e) => setzeEntwurf((alt) => ({ ...alt, bis: e.target.value }))}
            />
          </label>
        </div>
        {entwurf.von && entwurf.bis && entwurf.bis <= entwurf.von
          ? <p className="blatt-satz" style={{ color: "var(--stopp)", marginTop: 8 }}>{t("aufnahmen.terminUngueltig")}</p>
          : null}
        {aendern.isError ? (
          <FehlerBand
            pfad={fehlerPfad(aendern.error)} fehler={aendern.error}
            {...(fehlerWerte(aendern.error) ? { werte: fehlerWerte(aendern.error) as Record<string, string> } : {})}
          />
        ) : null}
      </div>
    </Blattvonunten>
  );
}

/**
 * Die Werte für `fehler.belegungUeberschneidung` — „{maschine} ist vom {von}
 * bis {bis} schon vergeben". Die API nennt die Person und den Zeitraum, der
 * im Weg liegt (`ueberschneidungMelden`).
 */
function fehlerWerte(e: unknown): Record<string, string> | undefined {
  if (!(e instanceof Fehler) || e.schluessel !== "belegung.ueberschneidung") return undefined;
  const d = e.daten;
  const im = Array.isArray(d["belegungen"]) ? (d["belegungen"][0] as Record<string, unknown> | undefined) : undefined;
  const von = String(im?.["planned_from"] ?? d["von"] ?? "");
  const bis = String(im?.["planned_until"] ?? d["bis"] ?? "");
  return {
    maschine: String(d["maschine"] ?? ""),
    von: von ? `${datumKurz(von)} ${uhr(von)}` : "",
    bis: bis ? `${datumKurz(bis)} ${uhr(bis)}` : "",
  };
}
